import React from 'react';
import Modal from '../../components/base/Modal';
import ViewModeToggle from '../../components/common/ViewModeToggle';
import InputLabel from '../../components/base/InputLabel';
import NumberInput from '../../components/base/NumberInput';
import MultiTpSettings from '../../components/metrics/MultiTpSettings';
import BigNumber from 'bignumber.js';
import { ERROR_MESSAGES } from '../../errors/constants';
import { MIN_VALUES } from '../../constants/minValues';

const SettingsModal = ({ isOpen, onClose, values, setValues, errors = {}, initialFocusField, viewMode, onViewModeChange }) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Settings"
    >
      <div className="space-y-6 pb-10">
        {/* View mode — Full / Compact (moved here from the metrics card) */}
        <div className="flex items-center justify-between">
          <InputLabel>View</InputLabel>
          <ViewModeToggle viewMode={viewMode} onChange={onViewModeChange} />
        </div>

        {/* Risk and Account Balance on the same level */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <div className="flex justify-between mb-2">
              <InputLabel required>Risk</InputLabel>
              <div className="flex gap-1">
                <button
                  onClick={() => setValues(prev => ({ ...prev, riskUnit: 'percent' }))}
                  className={`px-2.5 py-1 rounded-full text-xs transition-colors ${values.riskUnit === 'percent'
                    ? 'bg-primary-orange/20 text-primary-orange'
                    : 'hover:bg-slate-800 text-slate-500'
                    }`}
                >
                  %
                </button>
                <button
                  onClick={() => setValues(prev => ({ ...prev, riskUnit: 'fixed' }))}
                  className={`px-2.5 py-1 rounded-full text-xs transition-colors ${values.riskUnit === 'fixed'
                    ? 'bg-primary-orange/20 text-primary-orange'
                    : 'hover:bg-slate-800 text-slate-500'
                    }`}
                >
                  $
                </button>
              </div>
            </div>
            <NumberInput
              value={values.risk}
              onChange={(value) => setValues(prev => ({
                ...prev,
                risk: value || new BigNumber(0)
              }))}
              suffix={values.riskUnit === 'percent' ? '%' : undefined}
              prefix={values.riskUnit === 'fixed' ? '$' : undefined}
              decimals={2}
              required
              min={MIN_VALUES.risk}
              error={errors.risk ? { message: ERROR_MESSAGES.INVALID_RISK } : null}
            />
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <InputLabel required>Account Balance</InputLabel>
              <div className="h-[28px]"></div>
            </div>
            <NumberInput
              value={values.accountBalance}
              onChange={(value) => setValues(prev => ({
                ...prev,
                accountBalance: value || new BigNumber(0)
              }))}
              prefix="$"
              decimals={2}
              required
              min={MIN_VALUES.accountBalance}
              error={errors.accountBalance ? { message: ERROR_MESSAGES.INVALID_ACCOUNT_BALANCE } : null}
              autoFocus={initialFocusField === 'accountBalance'}
            />
          </div>
        </div>

        {/* R:R and Leverage */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <InputLabel required>Nominal R:R</InputLabel>
            <NumberInput
              value={values.nominalRR}
              onChange={(value) => setValues(prev => ({
                ...prev,
                nominalRR: value || new BigNumber(0)
              }))}
              suffix="x"
              decimals={1}
              required
              min={MIN_VALUES.nominalRR}
              error={errors.nominalRR ? { message: ERROR_MESSAGES.INVALID_RR } : null}
            />
          </div>

          <div>
            <InputLabel required>Max Leverage</InputLabel>
            <NumberInput
              value={values.maxLeverage}
              onChange={(value) => setValues(prev => ({
                ...prev,
                maxLeverage: value || new BigNumber(0)
              }))}
              suffix="x"
              decimals={0}
              required
              min={MIN_VALUES.maxLeverage}
              error={errors.maxLeverage ? { message: ERROR_MESSAGES.INVALID_LEVERAGE } : null}
            />
          </div>
        </div>

        {/* Fees */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <InputLabel required>Entry Fee</InputLabel>
            <NumberInput
              value={values.entryFee}
              onChange={(value) => setValues(prev => ({
                ...prev,
                entryFee: value || new BigNumber(0)
              }))}
              suffix="%"
              decimals={5}
              required
              min={MIN_VALUES.entryFee}
              error={errors.entryFee ? { message: ERROR_MESSAGES.INVALID_FEE } : null}
              autoFocus={initialFocusField === 'entryFee'}
            />
          </div>

          <div>
            <InputLabel required>Exit Fee</InputLabel>
            <NumberInput
              value={values.exitFee}
              onChange={(value) => setValues(prev => ({
                ...prev,
                exitFee: value || new BigNumber(0)
              }))}
              suffix="%"
              decimals={5}
              required
              min={MIN_VALUES.exitFee}
              error={errors.exitFee ? { message: ERROR_MESSAGES.INVALID_FEE } : null}
            />
          </div>
        </div>

        {/* Safety buffer (risk multiple) */}
        <div>
          <InputLabel required>Safety Buffer</InputLabel>
          <NumberInput
            value={values.safetyBufferR}
            onChange={(value) => setValues(prev => ({
              ...prev,
              safetyBufferR: value || MIN_VALUES.safetyBufferR
            }))}
            suffix="R"
            decimals={2}
            step={0.25}
            required
            min={MIN_VALUES.safetyBufferR}
            error={errors.safetyBufferR ? { message: ERROR_MESSAGES.INVALID_MARGIN } : null}
          />
          <p className="text-xs text-slate-500 mt-1.5">
            Keeps liquidation this many stop-distances beyond your stop — 2R = two stops past it.
          </p>
        </div>

        <MultiTpSettings values={values} onChange={(p) => setValues((prev) => ({ ...prev, ...p }))} />
      </div>
    </Modal>
  );
};

export default SettingsModal;